import { ReactNode, useMemo } from 'react'

import { featureFlags as featureFlagsMock } from '@/mocks'
import { useQuery } from '@api/hooks'
import { FeatureFlag } from '@domain'
import { createLDReactProvider, LDContext } from '@launchdarkly/react-sdk'
import FeatureFlagsApi, { FeatureFlagsKey } from '@services/featureFlags.api'

import { FeatureFlagsContext } from './FeatureFlagContext'
import { useMe } from './MeProvider'

type Props = {
	children: ReactNode
}

/**
 * Provide a context for the app infrastructure feature flags.
 */
export default function FeatureFlagProvider({ children }: Props) {
	const me = useMe()

	const ldContext: LDContext = useMemo(
		() => ({
			kind: 'user' as const,
			key: me.id,
			email: me.email,
			custom: {
				// Feature group needs to be added to the user create screen and saved in db
				// featureGroup: 'DEV',
				appEnv: import.meta.env.VITE_ENVIRONMENT,
			},
		}),
		[me.id, me.email]
	)

	const LDProvider = useMemo(
		() => createLDReactProvider(import.meta.env.VITE_LAUNCHDARKLY, ldContext),
		[ldContext]
	)

	// Not using a service file for this because it is not shared with other components
	const { data, isRefreshing, isFetching } = useQuery({
		queryKey: FeatureFlagsKey.all,
		queryFn: () => FeatureFlagsApi.getAll(),
	})

	const memoizedValue = useMemo(
		() => ({ flags: data, isRefreshing, isFetching }),
		[data, isRefreshing, isFetching]
	)

	return (
		<LDProvider>
			<FeatureFlagsContext.Provider value={memoizedValue}>{children}</FeatureFlagsContext.Provider>
		</LDProvider>
	)
}

// FIXME (rl) - Remove all the following when the LD migration is done
type FeatureFlagsMockerProviderProps = {
	children: ReactNode
	flags?: FeatureFlag[]
}

export function FeatureFlagsMockerProvider({
	children,
	flags = featureFlagsMock,
}: FeatureFlagsMockerProviderProps) {
	const memoizedValue = useMemo(() => ({ flags, isRefreshing: false, isFetching: false }), [flags])

	// No need to mock LDProvider here. This mock will go away.
	// Storybook and vitest have packages to do the mocking.
	return (
		<FeatureFlagsContext.Provider value={memoizedValue}>{children}</FeatureFlagsContext.Provider>
	)
}
